/**
 * Finding, waiting for, and tracking the live element a tour step points at.
 *
 * This is the load-bearing module. A capture-and-replay demo cannot break
 * because its screens are frozen; a tour that drives the real app breaks the
 * moment a component is renamed. Three ideas contain that:
 *
 * 1. **Ordered candidates.** Each anchor kind yields a list of selectors, best
 *    first, and the resolver reports *which one matched*. A step quietly
 *    sliding from `data-fg-tour` down to `data-testid` shows up in telemetry
 *    before the testid is deleted out from under it.
 * 2. **Visibility, not existence.** A mounted-but-empty element with a 0x0 rect
 *    is the common false positive — the component rendered, the data has not.
 * 3. **A timeout is a downgrade, never a crash.** `waitForAnchor` resolves with
 *    `null`, the engine applies the step's `fallback`, and `tour.anchor_missed`
 *    fires. Nothing a prospect can see ever hangs on a selector.
 */

import type { Rect } from './position'
import { DEFAULT_ANCHOR_TIMEOUT_MS, type TourAnchor, type TourAnchorKind } from './tourTypes'

/** Attributes worth re-querying on — anything else is noise in the observer. */
const OBSERVED_ATTRIBUTES = ['data-fg-tour', 'data-testid', 'data-cfo-segment-value', 'data-surface', 'class', 'hidden']

export type AnchorMatch = {
  element: HTMLElement
  /** Which candidate hit, so a downgrade is visible rather than silent. */
  matchedKind: TourAnchorKind
  matchedSelector: string
}

const cssEscape = (value: string): string => {
  const g = globalThis as { CSS?: { escape?: (v: string) => string } }
  if (typeof g.CSS?.escape === 'function') return g.CSS.escape(value)
  return String(value).replace(/["\\\]]/g, '\\$&')
}

/**
 * Ordered selector candidates for an anchor, best first.
 *
 * `data-fg-tour` is the contract we control. `data-testid` is borrowed from the
 * test suite — good enough to author against on day one, which is what lets the
 * convention spread incrementally instead of needing a 400-element audit first.
 */
export function anchorCandidates(anchor: TourAnchor): Array<{ kind: TourAnchorKind; selector: string }> {
  switch (anchor.kind) {
    case 'tour':
      return [{ kind: 'tour', selector: `[data-fg-tour="${cssEscape(anchor.value)}"]` }]
    case 'testid':
      return [
        // Prefer a data-fg-tour of the same name if someone has since added one.
        { kind: 'tour', selector: `[data-fg-tour="${cssEscape(anchor.value)}"]` },
        { kind: 'testid', selector: `[data-testid="${cssEscape(anchor.value)}"]` },
      ]
    case 'segment':
      return [{
        kind: 'segment',
        selector: `[data-cfo-segment="${cssEscape(anchor.control)}"][data-cfo-segment-value="${cssEscape(anchor.value)}"]`,
      }]
    case 'surface':
      return [{ kind: 'surface', selector: `[data-surface="${cssEscape(anchor.value)}"]` }]
    case 'nav':
      return [
        { kind: 'tour', selector: `[data-fg-tour="nav.${cssEscape(anchor.value)}"]` },
        { kind: 'nav', selector: `a[href$="/cfo/${cssEscape(anchor.value)}"]` },
      ]
    case 'text':
      // No selector can express an accessible name; resolveAnchor handles this
      // kind by scanning roles. The selector is only a coarse pre-filter.
      return [{ kind: 'text', selector: `[role="${cssEscape(anchor.role)}"], ${cssEscape(anchor.role)}` }]
    case 'css':
      return [{ kind: 'css', selector: anchor.value }]
    default:
      return []
  }
}

/** Below this an element is a rendering artifact, not a target. */
const MIN_ANCHOR_SIZE_PX = 8

/**
 * Visible enough to point at. Zero-area elements are rejected because "mounted
 * but still loading" is the single most common way a step would otherwise
 * attach to nothing — and the floor is 8px, not 1px, because a mid-layout
 * cockpit once presented its decision cards at 2x973 while the shell column
 * was still collapsing. Spotlighting a 2px sliver is worse than waiting.
 */
export function isAnchorVisible(el: Element | null | undefined): el is HTMLElement {
  if (!el || !(el instanceof HTMLElement) || !el.isConnected) return false
  const rect = el.getBoundingClientRect()
  if (rect.width < MIN_ANCHOR_SIZE_PX || rect.height < MIN_ANCHOR_SIZE_PX) return false
  const style = el.ownerDocument?.defaultView?.getComputedStyle(el)
  if (!style) return true
  return style.visibility !== 'hidden' && style.display !== 'none'
}

const accessibleName = (el: HTMLElement): string =>
  (el.getAttribute('aria-label') || el.textContent || '').trim().replace(/\s+/g, ' ').toLowerCase()

/** Single synchronous resolution attempt. Returns `null` when nothing matches. */
export function resolveAnchor(anchor: TourAnchor, root: ParentNode = document): AnchorMatch | null {
  const scope: ParentNode = (() => {
    if (!anchor.within) return root
    const parent = resolveAnchor(anchor.within, root)
    return parent ? parent.element : root
  })()
  // A declared `within` that has not rendered yet must not silently fall back to
  // the whole document — that is how a step lands on the wrong element.
  if (anchor.within && scope === root) return null

  for (const candidate of anchorCandidates(anchor)) {
    let matches: HTMLElement[]
    try {
      matches = Array.from(scope.querySelectorAll(candidate.selector)).filter(isAnchorVisible)
    } catch {
      continue // authored CSS can be syntactically invalid
    }
    if (anchor.kind === 'text') {
      const wanted = anchor.name.trim().toLowerCase()
      matches = matches.filter((el) => accessibleName(el).includes(wanted))
    }
    const picked = typeof anchor.nth === 'number' ? matches[anchor.nth] : matches[0]
    if (picked) return { element: picked, matchedKind: candidate.kind, matchedSelector: candidate.selector }
  }
  return null
}

/** How many visible elements an anchor matches — the editor's ambiguity check. */
export function countAnchorMatches(anchor: TourAnchor, root: ParentNode = document): number {
  const scope: ParentNode = anchor.within ? (resolveAnchor(anchor.within, root)?.element ?? root) : root
  if (anchor.within && scope === root) return 0
  for (const candidate of anchorCandidates(anchor)) {
    try {
      let matches = Array.from(scope.querySelectorAll(candidate.selector)).filter(isAnchorVisible)
      if (anchor.kind === 'text') {
        const wanted = anchor.name.trim().toLowerCase()
        matches = matches.filter((el) => accessibleName(el).includes(wanted))
      }
      if (matches.length) return matches.length
    } catch {
      continue
    }
  }
  return 0
}

export type WaitForAnchorOptions = {
  timeoutMs?: number
  signal?: AbortSignal
  root?: ParentNode
}

/**
 * Resolve now, or watch until the element appears.
 *
 * Composes an immediate query, one `MutationObserver` (rAF-coalesced so a busy
 * subtree cannot spin the CPU), a re-arm on the app's `fg_route_change` event,
 * and a hard timeout. Resolves `null` on timeout or abort — never rejects, so
 * no caller needs a try/catch around a step transition.
 */
export function waitForAnchor(anchor: TourAnchor, options: WaitForAnchorOptions = {}): Promise<AnchorMatch | null> {
  const root = options.root || document
  const timeoutMs = options.timeoutMs ?? anchor.timeoutMs ?? DEFAULT_ANCHOR_TIMEOUT_MS

  const immediate = resolveAnchor(anchor, root)
  if (immediate) return Promise.resolve(immediate)
  if (options.signal?.aborted) return Promise.resolve(null)

  return new Promise((resolve) => {
    let done = false
    let timer: ReturnType<typeof setTimeout> | null = null
    let poll: ReturnType<typeof setInterval> | null = null
    let observer: MutationObserver | null = null

    const finish = (match: AnchorMatch | null) => {
      if (done) return
      done = true
      if (timer) clearTimeout(timer)
      if (poll) clearInterval(poll)
      observer?.disconnect()
      options.signal?.removeEventListener('abort', onAbort)
      window.removeEventListener('fg_route_change', onTick)
      resolve(match)
    }

    // Coalesce with a clock, never with requestAnimationFrame. In a hidden or
    // occluded tab rAF does not fire, so an rAF-gated tick parks one callback
    // forever and every later mutation, poll and route tick becomes a no-op —
    // the resolver deadlocks until the timeout while its target sits matched
    // in the DOM. Opening a tour link in a background tab (cmd-click from an
    // email) is exactly that shape, and it degraded every anchored step to a
    // modal before the visitor ever looked. CFO pages mutate heavily while
    // loading, so bursts still need damping: an 80ms floor caps this at ~12
    // querySelectorAll passes per second, and setTimeout still runs hidden.
    let lastCheck = 0
    const onTick = () => {
      if (done) return
      const now = Date.now()
      if (now - lastCheck < 80) return
      lastCheck = now
      const match = resolveAnchor(anchor, root)
      if (match) finish(match)
    }

    const onAbort = () => finish(null)

    observer = new MutationObserver(onTick)
    observer.observe(root instanceof Document ? root.documentElement : (root as Element), {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: OBSERVED_ATTRIBUTES,
    })
    window.addEventListener('fg_route_change', onTick)
    // Mutations are not the only way an element becomes visible. The CEO
    // cockpit's decision card mounted at 0x0 and grew through layout alone —
    // no childList or attribute change — so the observer never re-checked and
    // the step degraded while the element sat there fully rendered. Cheap
    // clock-based re-checks close that class entirely.
    poll = setInterval(onTick, 300)
    options.signal?.addEventListener('abort', onAbort)
    timer = setTimeout(() => finish(null), timeoutMs)
    onTick()
  })
}

export const rectOf = (el: Element): Rect => {
  const r = el.getBoundingClientRect()
  return { x: r.x, y: r.y, width: r.width, height: r.height }
}

/**
 * Every scrollable ancestor of `el`.
 *
 * The CFO shell puts `overflow: auto` on `.cfo-shell-main`, so the *page* never
 * scrolls — a `window` scroll listener would never fire and the card would
 * drift off its anchor. Listening on the real scroll containers is what keeps
 * the overlay pinned.
 */
export function scrollableAncestors(el: Element): Element[] {
  const out: Element[] = []
  const view = el.ownerDocument?.defaultView
  let node: Element | null = el.parentElement
  while (node && node !== el.ownerDocument?.documentElement) {
    const style = view?.getComputedStyle(node)
    const overflow = `${style?.overflowY || ''} ${style?.overflowX || ''}`
    if (/(auto|scroll|overlay)/.test(overflow)) out.push(node)
    node = node.parentElement
  }
  return out
}

const prefersReducedMotion = (): boolean => {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  } catch {
    return false
  }
}

/**
 * Bring the anchor into view and wait for scrolling to actually stop.
 *
 * Positions the anchor at ~35% of the container height rather than dead centre,
 * so a tooltip placed below it still has room. Resolves after `scrollend` where
 * supported, and otherwise once the rect has held still for two frames.
 */
export async function scrollAnchorIntoView(el: HTMLElement, signal?: AbortSignal): Promise<void> {
  const reduced = prefersReducedMotion()
  const container = scrollableAncestors(el)[0]
  const behavior: ScrollBehavior = reduced ? 'auto' : 'smooth'

  if (container instanceof HTMLElement) {
    const cRect = container.getBoundingClientRect()
    const eRect = el.getBoundingClientRect()
    const target = container.scrollTop + (eRect.top - cRect.top) - cRect.height * 0.35
    const max = container.scrollHeight - container.clientHeight
    const next = Math.max(0, Math.min(target, max));
    if (Math.abs(next - container.scrollTop) > 2) container.scrollTo({ top: next, behavior })
  } else {
    el.scrollIntoView({ block: 'center', inline: 'nearest', behavior })
  }

  if (reduced) return
  await settled(el, signal)
}

/** Resolve once the element's rect has been stable for two consecutive frames. */
function settled(el: HTMLElement, signal?: AbortSignal, timeoutMs = 700): Promise<void> {
  return new Promise((resolve) => {
    let done = false
    let stable = 0
    let last: Rect | null = null
    let frame = 0
    const finish = () => {
      if (done) return
      done = true
      if (frame) cancelAnimationFrame(frame)
      clearTimeout(timer)
      signal?.removeEventListener('abort', finish)
      resolve()
    }
    const timer = setTimeout(finish, timeoutMs)
    signal?.addEventListener('abort', finish)
    const tick = () => {
      if (done) return
      const now = rectOf(el)
      stable = last && last.x === now.x && last.y === now.y ? stable + 1 : 0
      last = now
      if (stable >= 2) return finish()
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
  })
}

/**
 * The rail's publish-time attestation, computed in the founder's own browser.
 *
 * It can only see the CURRENT page — an authoring session is not a headless
 * multi-route walk — so it verifies the steps that anchor here and records the
 * rest as `deferred`. The result becomes the `anchor_check` payload on publish
 * when (and only when) it passes, letting the server's gate clear without the
 * CI verifier having uploaded first.
 *
 * The one rule that keeps this honest: a check that verified nothing is NOT a
 * pass. If the author is parked on a page where no step anchors, `verified` is
 * empty and the status is `fail` — otherwise "publish" would attest to a tour
 * it never looked at, which is exactly the always-green check that reads like a
 * working one.
 */
export interface RailAnchorCheck {
  status: 'pass' | 'fail'
  coverage: 'current-page'
  source: 'rail'
  page: string
  verified: Array<{ id: string; matches: number }>
  failures: Array<{ id: string; matches: number }>
  deferred: string[]
  modals: number
  reason?: string
}

export function buildRailAnchorCheck(
  doc: { chapters: Array<{ steps: Array<{ id: string; anchor: TourAnchor | null; route: { page: string } }> }> },
  pathname: string,
  root: ParentNode = document,
): RailAnchorCheck {
  const page = pathname.split('/').filter(Boolean).pop() || ''
  const verified: Array<{ id: string; matches: number }> = []
  const failures: Array<{ id: string; matches: number }> = []
  const deferred: string[] = []
  let modals = 0
  for (const chapter of doc.chapters) {
    for (const step of chapter.steps) {
      if (!step.anchor) { modals++; continue }
      if (step.route.page !== page) { deferred.push(step.id); continue }
      const n = countAnchorMatches(step.anchor, root)
      if (n === 1) verified.push({ id: step.id, matches: n })
      else failures.push({ id: step.id, matches: n })
    }
  }
  const base = { coverage: 'current-page' as const, source: 'rail' as const, page, verified, failures, deferred, modals }
  if (failures.length) {
    const worst = failures[0]
    return { ...base, status: 'fail', reason: `Step "${worst.id}" matches ${worst.matches} elements on /${page} — fix the anchor before publishing.` }
  }
  if (verified.length === 0) {
    return { ...base, status: 'fail', reason: `No tour steps anchor on /${page}. Open the tour's start page (or Play from here), or run \`npm run tour:verify-anchors\` for the full multi-page check.` }
  }
  return { ...base, status: 'pass' }
}
